import { useCallback, useEffect, useRef, useState } from 'react';
import type { AndroidUpdateOffer } from '../core/config/androidUpdate';
import { isAndroidNativeRuntime } from '../adapters/mobile/apkInstaller';
import {
  checkAndroidAppUpdate,
  dismissAndroidUpdateVersion,
  downloadAndInstallAndroidUpdate,
  readDismissedAndroidUpdateVersion,
  type AndroidUpdateProgress,
} from '../services/mobile/androidAppUpdate';
import { useRemoteConfig } from './useRemoteConfig';
import { useAndroidUpdateResume } from './useAndroidUpdateResume';

export type AndroidUpdateUiPhase =
  | 'idle'
  | 'checking'
  | 'available'
  | 'preparing'
  | 'downloading'
  | 'installing'
  | 'error';

function mapInstallPhase(
  progress: AndroidUpdateProgress,
  setPhase: (phase: AndroidUpdateUiPhase) => void,
) {
  if (progress.phase === 'preparing') setPhase('preparing');
  if (progress.phase === 'downloading') setPhase('downloading');
  if (progress.phase === 'installing') setPhase('installing');
  if (progress.phase === 'error') setPhase('error');
}

export function useAndroidAppUpdate(options: { enabled?: boolean } = {}) {
  const enabled = options.enabled !== false && isAndroidNativeRuntime();
  const remote = useRemoteConfig();
  const [offer, setOffer] = useState<AndroidUpdateOffer | null>(null);
  const [phase, setPhase] = useState<AndroidUpdateUiPhase>('idle');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const offerRef = useRef<AndroidUpdateOffer | null>(null);
  const pendingPermissionRetryRef = useRef(false);

  useEffect(() => {
    offerRef.current = offer;
  }, [offer]);

  const refresh = useCallback(async () => {
    if (!enabled) return null;
    setPhase('checking');
    try {
      const next = await checkAndroidAppUpdate({ minAppVersion: remote.minAppVersion });
      if (!next?.available) {
        setOffer(null);
        setVisible(false);
        setPhase('idle');
        return null;
      }
      const dismissed = readDismissedAndroidUpdateVersion();
      if (!next.force && dismissed === next.remote.versionName) {
        setOffer(next);
        setVisible(false);
        setPhase('idle');
        return next;
      }
      setOffer(next);
      setVisible(true);
      setPhase('available');
      return next;
    } catch {
      setPhase('idle');
      return null;
    }
  }, [enabled, remote.minAppVersion]);

  const update = useCallback(async (overrideOffer?: AndroidUpdateOffer | null) => {
    const target = overrideOffer ?? offerRef.current;
    if (!target) return false;
    setErrorKey(null);
    const result = await downloadAndInstallAndroidUpdate(
      target,
      (progress) => mapInstallPhase(progress, setPhase),
    );
    if (!result.ok) {
      if (result.reason === 'install_permission_required') {
        pendingPermissionRetryRef.current = true;
      }
      setPhase('error');
      setErrorKey(result.reason ?? 'install_failed');
      setVisible(true);
      return false;
    }
    pendingPermissionRetryRef.current = false;
    setPhase('installing');
    return true;
  }, []);

  useAndroidUpdateResume({
    enabled,
    pendingPermissionRetryRef,
    offerRef,
    refresh,
    update,
  });

  const dismiss = useCallback(() => {
    if (!offer || offer.force) return;
    dismissAndroidUpdateVersion(offer.remote.versionName);
    setVisible(false);
    setPhase('idle');
  }, [offer]);

  return {
    enabled, offer, visible, phase, errorKey,
    force: Boolean(offer?.force), refresh, dismiss, update,
  };
}
